import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, KanbanSquare, Activity } from "lucide-react";

const stageMeta = {
  input:   { label: "Input",   color: "text-kinetic", chip: "bg-kinetic/10 border-kinetic/30 text-kinetic" },
  process: { label: "Process", color: "text-white",  chip: "bg-white/5 border-white/20 text-white" },
  output:  { label: "Output",  color: "text-coral",  chip: "bg-coral/10 border-coral/30 text-coral" },
  closure: { label: "Closure", color: "text-neon",   chip: "bg-neon/10 border-neon/30 text-neon" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    (async () => {
      const [a, p] = await Promise.all([api.analytics(), api.listProjects()]);
      setData(a);
      setProjects(p.slice(0, 6));
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-white/40">{user?.role === "admin" ? "AN INFLUENCER FACTORY · CONTROL ROOM" : "AN INFLUENCER FACTORY · STUDIO"}</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">Hi, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-white/60 mt-2">Here&apos;s your factory status today.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/factory/projects"><Button data-testid="dash-new-project" className="kinetic-btn rounded-sm">+ New Project</Button></Link>
          <Link to="/app/factory/ai"><Button data-testid="dash-ai" variant="outline" className="rounded-sm border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
            <Sparkles className="w-4 h-4 mr-2" /> AI Studio
          </Button></Link>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
        {[
          ["PROJECTS", data?.projects_total ?? "—"],
          ["REVENUE", data ? `₹${data.revenue.toLocaleString('en-IN')}` : "—"],
          ["OUTSTANDING", data ? `₹${data.outstanding.toLocaleString('en-IN')}` : "—"],
          ["LEADS", data?.leads_total ?? "—"],
        ].map(([k, v]) => (
          <div key={k} className="bg-[#0E0E0E] p-6">
            <div className="mono text-white/40">{k}</div>
            <div className="font-sans text-3xl font-extrabold mt-3" data-testid={`kpi-${k.toLowerCase()}`}>{v}</div>
          </div>
        ))}
      </div>

      {/* Stages overview */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-white/10 bg-[#0C0C0C]">
          <div className="border-b border-white/10 px-5 py-3 flex items-center justify-between">
            <span className="mono text-white/60 flex items-center gap-2"><KanbanSquare className="w-3 h-3" /> PIPELINE</span>
            <Link to="/app/factory/pipeline" className="mono text-kinetic hover:underline">OPEN BOARD →</Link>
          </div>
          <div className="grid grid-cols-4 divide-x divide-white/10">
            {Object.entries(stageMeta).map(([k, m]) => (
              <div key={k} className="p-5">
                <div className={`mono ${m.color}`}>{m.label}</div>
                <div className="font-sans text-4xl font-extrabold mt-3">{data?.stages?.[k] ?? 0}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 bg-[#0C0C0C]">
          <div className="border-b border-white/10 px-5 py-3 mono text-white/60 flex items-center gap-2">
            <Activity className="w-3 h-3" /> LEAD FUNNEL
          </div>
          <div className="p-5 space-y-3">
            {data && Object.entries(data.lead_stages).map(([k, v]) => {
              const total = data.leads_total || 1;
              const pct = Math.round((v / total) * 100);
              return (
                <div key={k}>
                  <div className="flex justify-between mono text-white/60"><span>{k}</span><span>{v} · {pct}%</span></div>
                  <div className="h-1.5 bg-white/5 mt-1 overflow-hidden">
                    <div className="h-full bg-kinetic" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent projects */}
      <div className="border border-white/10 bg-[#0C0C0C]">
        <div className="border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <span className="mono text-white/60">RECENT PROJECTS</span>
          <Link to="/app/factory/projects" className="mono text-kinetic hover:underline">VIEW ALL →</Link>
        </div>
        <div className="divide-y divide-white/10">
          {projects.length === 0 && <div className="p-8 text-white/50 text-center">No projects yet. Create your first one →</div>}
          {projects.map(p => (
            <Link to={`/app/factory/projects/${p.id}`} key={p.id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 px-5 py-4 hover:bg-white/[0.03] transition" data-testid={`recent-${p.id}`}>
              <div>
                <div className="font-sans font-bold">{p.title}</div>
                <div className="mono text-white/40 mt-1">{p.client_name}</div>
              </div>
              <span className={`stage-pill ${stageMeta[p.stage].chip}`}>{stageMeta[p.stage].label}</span>
              <span className="mono text-white/50 hidden md:inline">₹{(p.budget||0).toLocaleString('en-IN')}</span>
              <ArrowRight className="w-4 h-4 text-white/40" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
